import { Button } from "@/components/ui/button";
import { FileText, Loader2, CheckCircle2, XCircle, Eye } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';

interface PdfGeneratorProps {
    isPdfGenerating: boolean;
    pdfBase64: string | null;
    pdfFilename: string | null;
    pdfError: string | null;
    onGeneratePdf: () => void;
    onPreviewPdf: () => void;
    generateDisabled?: boolean;
}

export const PdfGenerator = ({
    isPdfGenerating,
    pdfBase64,
    pdfFilename,
    pdfError,
    onGeneratePdf,
    onPreviewPdf,
    generateDisabled = false,
}: PdfGeneratorProps) => {
    const { t } = useI18n();
    const isPdfReady = !isPdfGenerating && pdfBase64 !== null;

    return (
        <div className="space-y-4">
            {!isPdfReady && !isPdfGenerating && (
                <div className="flex justify-center pt-2">
                    <Button
                        onClick={onGeneratePdf}
                        disabled={isPdfGenerating || generateDisabled}
                        className="px-6 py-3 bg-black text-white hover:bg-gray-800 flex items-center gap-2"
                    >
                        <FileText className="h-4 w-4" />
                        {t('itinerary.generatePdfButton')}
                    </Button>
                </div>
            )}

            {(isPdfGenerating || isPdfReady || pdfError) && (
                <div className={`rounded-lg my-4 border p-4 flex items-center justify-between gap-4 transition-colors ${
                    isPdfGenerating
                        ? 'border-gray-200 bg-gray-50'
                        : isPdfReady
                            ? 'border-green-200 bg-green-50'
                            : pdfError
                                ? 'border-red-200 bg-red-50'
                                : 'border-gray-200 bg-gray-50'
                }`}>
                    <div className="flex items-center gap-3 min-w-0">
                        {isPdfGenerating && (
                            <>
                                <Loader2 className="h-5 w-5 text-gray-400 animate-spin flex-shrink-0" />
                                <div>
                                    <p className="font-medium text-gray-700">{t('itinerary.generatingPDF')}</p>
                                    <p className="text-sm text-gray-500">{t('itinerary.buildingDocument')}</p>
                                </div>
                            </>
                        )}
                        {isPdfReady && (
                            <>
                                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="font-medium text-green-800">{t('itinerary.pdfReady')}</p>
                                    <p className="text-sm text-green-600 truncate">{pdfFilename}</p>
                                </div>
                            </>
                        )}
                        {pdfError && !isPdfGenerating && (
                            <>
                                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                                <div>
                                    <p className="font-medium text-red-800">{t('itinerary.pdfGenerationError')}</p>
                                    <p className="text-sm text-red-600">{t('itinerary.unableToGenerate')}</p>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isPdfReady && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onPreviewPdf}
                                className="flex items-center gap-1.5 border-green-300 text-green-700 hover:bg-green-100 hover:border-green-400"
                            >
                                <Eye className="h-3.5 w-3.5" />
                                {t('common.preview')}
                            </Button>
                        )}
                        {pdfError && !isPdfGenerating && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onGeneratePdf}
                                className="flex items-center gap-1.5"
                            >
                                {t('common.retry')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
